/* -----------------------------------------------------------------------
 * Nonogram Board — Canvas 2D Renderer
 *
 * Single HTML canvas, zero per-cell React components.
 * All drawing via ctx.fillRect / ctx.fillText — works for any grid size.
 * ----------------------------------------------------------------------- */

import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useCallback,
  useEffect,
  type RefObject,
  type KeyboardEvent,
} from 'react';
import { CellState } from '../types';

/* ---------- public types ---------- */

export interface BoardParticle {
  id: number;
  x: number;
  y: number;
  dx: number;
  dy: number;
  color: string;
  delay: number;
}

export interface BoardGridHandle {
  setHover: (row: number, col: number) => void;
}

export interface BoardGridProps {
  size: number;
  cols?: number;
  inputMode?: 'fill' | 'mark' | 'hint';
  rowClues: number[][];
  colClues: number[][];
  cellSize: number;
  grid: CellState[][];
  completedRows?: boolean[];
  completedCols?: boolean[];
  /** Per-clue completion for rows: rowClueDone[row][clueIndex]. */
  rowClueDone?: boolean[][];
  /** Per-clue completion for cols: colClueDone[col][clueIndex]. */
  colClueDone?: boolean[][];
  errorCells?: Set<string>;
  /** Per-cell error tint color, keyed by "row,col". Falls back to default warm tint. */
  errorColors?: Map<string, string>;
  /** Whether to animate error cells (shake). Default true. */
  animateErrors?: boolean;
  /** Whether to show static error markers (red background). Default false. */
  markErrors?: boolean;
  /** Whether to animate correct cells (green flash). Default true. */
  animateCorrect?: boolean;
  flashRows?: Set<number>;
  flashCols?: Set<number>;
  focusRow?: number;
  focusCol?: number;
  solved?: boolean;
  solution?: boolean[][];
  lastAction?: { row: number; col: number; type: string; id: number };
  particles?: BoardParticle[];
  highlightCells?: Set<string>;
  /** Flash intensity for valid cells (0-1, where 1 = full green). */
  validFlash?: number;
  /** Which cells to flash (if empty, all non-empty cells flash). */
  validFlashCells?: Set<string>;
  /** Whether to show crosshair highlight on hover. Default true. */
  showCrosshair?: boolean;
  /** Show minimum-span hints in clue headers (sum + gaps). */
  showSpanHints?: boolean;
  /** Called on pointer down and during drag. Return false to stop the drag. */
  onCellDown?: (row: number, col: number, button: number) => void | boolean;
  onCellMove?: (row: number, col: number) => void;
  onCellUp?: () => void;
  /** When true, onCellDown is called for each new cell during drag (instead of onCellMove). */
  dragRepeatsDown?: boolean;
  gridRef?: RefObject<HTMLDivElement | null>;
  tabIndex?: number;
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
  onFocus?: () => void;
}

/* ---------- defaults ---------- */

const EMPTY_SET = new Set<string>();
const EMPTY_NUM_SET = new Set<number>();

/* ---------- colors ---------- */

const COL_FILLED = '#f0b830';
const COL_VALID = '#4caf50';
const COL_ERROR = '#db7b7b'; // warm pastel red, complements #f0b830 yellow

// Interpolate between two hex colors
function lerpColor(a: string, b: string, t: number): string {
  const ah = parseInt(a.slice(1), 16);
  const bh = parseInt(b.slice(1), 16);
  const ar = (ah >> 16) & 0xff, ag = (ah >> 8) & 0xff, ab = ah & 0xff;
  const br = (bh >> 16) & 0xff, bg = (bh >> 8) & 0xff, bb = bh & 0xff;
  const rr = Math.round(ar + (br - ar) * t);
  const rg = Math.round(ag + (bg - ag) * t);
  const rb = Math.round(ab + (bb - ab) * t);
  return `#${((rr << 16) | (rg << 8) | rb).toString(16).padStart(6, '0')}`;
}
const COL_FLASH = '#fff5e0';
const COL_X = '#b87830';
const COL_DIM = '#f5f0e8';
const COL_BG = '#FFF5E1';
const COL_CLUE = '#5a3a1a';
const COL_CLUE_DONE = '#c8b8a0';
const COL_CROSSHAIR_FILL = 'rgba(210, 190, 140, 0.3)';
const COL_CROSSHAIR_MARK = 'rgba(184, 120, 48, 0.22)';
const COL_CROSSHAIR_HINT = 'rgba(42, 108, 181, 0.18)';
const COL_GRID = '#c8b898';
const COL_SEP = '#a89060';

function crosshairColorForMode(mode: 'fill' | 'mark' | 'hint'): string {
  switch (mode) {
    case 'mark':
      return COL_CROSSHAIR_MARK;
    case 'hint':
      return COL_CROSSHAIR_HINT;
    case 'fill':
    default:
      return COL_CROSSHAIR_FILL;
  }
}

/* ---------- drawing helpers ---------- */

function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  corners: { tl?: number; tr?: number; bl?: number; br?: number }
) {
  const { tl = 0, tr = 0, bl = 0, br = 0 } = corners;
  ctx.beginPath();
  ctx.moveTo(x + tl, y);
  ctx.lineTo(x + w - tr, y);
  if (tr) ctx.quadraticCurveTo(x + w, y, x + w, y + tr);
  else ctx.lineTo(x + w, y);
  ctx.lineTo(x + w, y + h - br);
  if (br) ctx.quadraticCurveTo(x + w, y + h, x + w - br, y + h);
  else ctx.lineTo(x + w, y + h);
  ctx.lineTo(x + bl, y + h);
  if (bl) ctx.quadraticCurveTo(x, y + h, x, y + h - bl);
  else ctx.lineTo(x, y + h);
  ctx.lineTo(x, y + tl);
  if (tl) ctx.quadraticCurveTo(x, y, x + tl, y);
  else ctx.lineTo(x, y);
  ctx.closePath();
}

function drawX(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  thickness: number,
  color: string = COL_X
) {
  const hs = size / 2;
  ctx.lineWidth = thickness;
  ctx.strokeStyle = color;
  ctx.lineCap = 'round';
  ctx.globalAlpha = 0.8;
  ctx.beginPath();
  ctx.moveTo(cx - hs, cy - hs);
  ctx.lineTo(cx + hs, cy + hs);
  ctx.moveTo(cx + hs, cy - hs);
  ctx.lineTo(cx - hs, cy + hs);
  ctx.stroke();
  ctx.globalAlpha = 1;
}

/* ---------- BoardGrid ---------- */

export const BoardGrid = forwardRef<BoardGridHandle, BoardGridProps>(
  function BoardGrid(
    {
      size,
      cols: colsProp,
      inputMode = 'fill',
      rowClues,
      colClues,
      cellSize,
      grid,
      completedRows = [],
      completedCols = [],
      rowClueDone,
      colClueDone,
      errorCells = EMPTY_SET,
      errorColors,
      animateErrors = true,
      markErrors = false,
      animateCorrect = true,
      flashRows = EMPTY_NUM_SET,
      flashCols = EMPTY_NUM_SET,
      focusRow = -1,
      focusCol = -1,
      solved = false,
      solution = [],
      lastAction = { row: -1, col: -1, type: '', id: 0 },
      particles,
      highlightCells,
      validFlash = 0,
      validFlashCells,
      showCrosshair = true,
      showSpanHints = false,
      onCellDown,
      onCellMove,
      onCellUp,
      dragRepeatsDown = false,
      gridRef,
      tabIndex,
      onKeyDown,
      onFocus,
    },
    handleRef
  ) {
    const gameCols = colsProp ?? size;
    const gameRows = size;
    const maxColClueLen = Math.max(...colClues.map((c) => c.length)) + (showSpanHints ? 1 : 0);
    const maxRowClueLen = Math.max(...rowClues.map((c) => c.length)) + (showSpanHints ? 1 : 0);
    const totalCols = maxRowClueLen + gameCols;
    const totalRows = maxColClueLen + gameRows;

    const boardW = cellSize * totalCols;

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const hoverRowRef = useRef(-1);
    const hoverColRef = useRef(-1);
    const shakeTimers = useRef<Map<string, number>>(new Map());
    const rafRef = useRef<number>(0);
    const lastTimeRef = useRef(0);

    // Store props in refs so the draw loop (mount-only effect) always has latest data
    const propsRef = useRef({
      grid,
      errorCells,
      errorColors,
      animateErrors,
      markErrors,
      animateCorrect,
      flashRows,
      flashCols,
      inputMode,
      solved,
      solution,
      rowClues,
      colClues,
      completedRows,
      completedCols,
      rowClueDone,
      colClueDone,
      gameRows,
      gameCols,
      totalRows,
      totalCols,
      maxColClueLen,
      maxRowClueLen,
      highlightCells,
      validFlash,
      validFlashCells,
      showCrosshair,
      showSpanHints,
      particles,
    });
    propsRef.current = {
      grid,
      errorCells,
      errorColors,
      animateErrors,
      markErrors,
      animateCorrect,
      flashRows,
      flashCols,
      inputMode,
      solved,
      solution,
      rowClues,
      colClues,
      completedRows,
      completedCols,
      rowClueDone,
      colClueDone,
      gameRows,
      gameCols,
      totalRows,
      totalCols,
      maxColClueLen,
      maxRowClueLen,
      highlightCells,
      validFlash,
      validFlashCells,
      showCrosshair,
      showSpanHints,
      particles,
    };

    const particleStartRef = useRef<number>(0);
    const prevParticlesRef = useRef(particles);
    if (particles !== prevParticlesRef.current) {
      prevParticlesRef.current = particles;
      if (particles && particles.length > 0) {
        particleStartRef.current = performance.now();
      }
    }

    const updateCrosshair = useCallback((row: number, col: number) => {
      hoverRowRef.current = row;
      hoverColRef.current = col;
    }, []);

    useImperativeHandle(handleRef, () => ({ setHover: updateCrosshair }), [
      updateCrosshair,
    ]);

    // Trigger shake for error cells (only if animateErrors is enabled)
    useEffect(() => {
      if (!animateErrors) return;
      errorCells.forEach((key) => {
        if (!shakeTimers.current.has(key)) shakeTimers.current.set(key, 0.5);
      });
    }, [errorCells, animateErrors]);

    // Compute cell coordinates from pixel position
    const cellFromPixel = useCallback(
      (px: number, py: number): { row: number; col: number } | null => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const x = px - rect.left;
        const y = py - rect.top;
        const cw = rect.width / totalCols;
        const ch = rect.height / totalRows;
        const gridCol = Math.floor(x / cw);
        const gridRow = Math.floor(y / ch);
        const col = gridCol - maxRowClueLen;
        const row = gridRow - maxColClueLen;
        if (row >= 0 && col >= 0 && row < gameRows && col < gameCols)
          return { row, col };
        return null;
      },
      [totalCols, totalRows, maxRowClueLen, maxColClueLen, gameRows, gameCols]
    );

    // Pointer event handlers — unified mouse + touch + pen
    const dragBtnRef = useRef(-1);
    const dragStoppedRef = useRef(false);

    const handlePointerDown = useCallback(
      (e: React.PointerEvent) => {
        e.preventDefault();
        (e.target as Element).setPointerCapture(e.pointerId);
        dragBtnRef.current = e.button;
        dragStoppedRef.current = false;
        const cell = cellFromPixel(e.clientX, e.clientY);
        if (cell) {
          updateCrosshair(cell.row, cell.col);
          const result = onCellDown?.(cell.row, cell.col, e.button);
          if (result === false) dragStoppedRef.current = true;
        }
      },
      [cellFromPixel, updateCrosshair, onCellDown]
    );

    const handlePointerMove = useCallback(
      (e: React.PointerEvent) => {
        const cell = cellFromPixel(e.clientX, e.clientY);
        if (cell) {
          if (
            cell.row !== hoverRowRef.current ||
            cell.col !== hoverColRef.current
          ) {
            updateCrosshair(cell.row, cell.col);
            if (dragRepeatsDown && dragBtnRef.current >= 0 && !dragStoppedRef.current) {
              const result = onCellDown?.(cell.row, cell.col, dragBtnRef.current);
              if (result === false) dragStoppedRef.current = true;
            } else {
              onCellMove?.(cell.row, cell.col);
            }
          }
        } else if (hoverRowRef.current >= 0 || hoverColRef.current >= 0) {
          updateCrosshair(-1, -1);
        }
      },
      [cellFromPixel, updateCrosshair, onCellMove, onCellDown, dragRepeatsDown]
    );

    const handlePointerUp = useCallback(
      (e: React.PointerEvent) => {
        (e.target as Element).releasePointerCapture(e.pointerId);
        dragBtnRef.current = -1;
        dragStoppedRef.current = false;
        onCellUp?.();
      },
      [onCellUp]
    );

    const handlePointerLeave = useCallback(() => {
      updateCrosshair(-1, -1);
    }, [updateCrosshair]);

    // Main draw loop — runs once on mount, reads latest data from propsRef
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const draw = (time: number) => {
        const dt = lastTimeRef.current
          ? (time - lastTimeRef.current) / 1000
          : 0.016;
        lastTimeRef.current = time;

        const p = propsRef.current;
        const dpr = window.devicePixelRatio || 1;
        const w = canvas.clientWidth;
        const h = canvas.clientHeight;
        if (w === 0 || h === 0) {
          rafRef.current = requestAnimationFrame(draw);
          return;
        }
        if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
          canvas.width = w * dpr;
          canvas.height = h * dpr;
        }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const cw = w / p.totalCols;
        const ch = h / p.totalRows;
        const cornerRadius = Math.min(cw, ch) * 0.14;

        // Clear
        ctx.fillStyle = COL_BG;
        ctx.fillRect(0, 0, w, h);

        // Crosshair highlight
        const hr = hoverRowRef.current;
        const hc = hoverColRef.current;
        const crosshairColor = p.showCrosshair ? crosshairColorForMode(p.inputMode) : null;
        if (crosshairColor && hr >= 0) {
          ctx.fillStyle = crosshairColor;
          ctx.fillRect(0, (p.maxColClueLen + hr) * ch, w, ch);
        }
        if (crosshairColor && hc >= 0) {
          ctx.fillStyle = crosshairColor;
          ctx.fillRect((p.maxRowClueLen + hc) * cw, 0, cw, h);
        }

        // Game cells
        for (let r = 0; r < p.gameRows; r++) {
          for (let c = 0; c < p.gameCols; c++) {
            const state = p.grid[r]?.[c] ?? CellState.Empty;
            const x = (p.maxRowClueLen + c) * cw;
            const y = (p.maxColClueLen + r) * ch;
            const key = `${r},${c}`;

            let ox = 0;
            const shake = shakeTimers.current.get(key);
            if (shake && shake > 0) {
              const t = shake / 0.5;
              ox = 0;
              shakeTimers.current.set(key, shake - dt);
              if (shake - dt <= 0) shakeTimers.current.delete(key);
            }

            if (state === CellState.Filled) {
              const isTL = r === 0 && c === 0;
              const isBR = r === p.gameRows - 1 && c === p.gameCols - 1;
              const isError = p.errorCells.has(key);
              // Only flash if cell was present when validate was triggered
              const shouldFlash =
                p.animateCorrect &&
                p.validFlash > 0 &&
                (!p.validFlashCells || p.validFlashCells.size === 0 || p.validFlashCells.has(key));
              // Use red for marked errors, green flash for correct (if enabled), otherwise yellow
              const fillColor =
                isError && p.markErrors
                  ? COL_ERROR
                  : shouldFlash
                    ? lerpColor(COL_FILLED, COL_VALID, p.validFlash)
                    : COL_FILLED;
              if (isTL || isBR) {
                ctx.fillStyle = fillColor;
                drawRoundedRect(ctx, x + ox, y, cw, ch, {
                  tl: isTL ? cornerRadius : 0,
                  br: isBR ? cornerRadius : 0,
                });
                ctx.fill();
              } else {
                ctx.fillStyle = fillColor;
                ctx.fillRect(x + ox, y, cw, ch);
              }
            } else if (p.flashRows.has(r) || p.flashCols.has(c)) {
              ctx.fillStyle = COL_FLASH;
              ctx.globalAlpha = 0.8;
              ctx.fillRect(x, y, cw, ch);
              ctx.globalAlpha = 1;
            } else if (p.solved && !p.solution[r]?.[c]) {
              ctx.fillStyle = COL_DIM;
              ctx.globalAlpha = 0.4;
              ctx.fillRect(x, y, cw, ch);
              ctx.globalAlpha = 1;
            }

            // Error tint on empty cells (gentle fade-out)
            if (state === CellState.Empty && shake && shake > 0) {
              const t = shake / 0.5;
              ctx.fillStyle = p.errorColors?.get(key) ?? '#e8a080';
              ctx.globalAlpha = 0.3 * t;
              ctx.fillRect(x, y, cw, ch);
              ctx.globalAlpha = 1;
            }

            // Tutorial highlight pulse on empty cells
            if (state === CellState.Empty && p.highlightCells?.has(key)) {
              const t = performance.now() / 1000;
              const pulse = 0.12 + Math.sin(((t % 2.4) / 2.4) * Math.PI * 2) * 0.1;
              ctx.fillStyle = COL_FILLED;
              ctx.globalAlpha = pulse;
              ctx.fillRect(x, y, cw, ch);
              ctx.globalAlpha = 1;
            }

            if (state === CellState.Marked) {
              const isError = p.errorCells.has(key);
              // Only flash if cell was present when validate was triggered
              const shouldFlash =
                p.animateCorrect &&
                p.validFlash > 0 &&
                (!p.validFlashCells || p.validFlashCells.size === 0 || p.validFlashCells.has(key));
              // Add red background for error X marks (only if markErrors enabled)
              if (isError && p.markErrors) {
                ctx.fillStyle = COL_ERROR;
                ctx.globalAlpha = 0.35;
                ctx.fillRect(x, y, cw, ch);
                ctx.globalAlpha = 1;
              } else if (shouldFlash) {
                // Green flash for correct X marks
                ctx.fillStyle = COL_VALID;
                ctx.globalAlpha = 0.35 * p.validFlash;
                ctx.fillRect(x, y, cw, ch);
                ctx.globalAlpha = 1;
              }
              // X stroke unchanged - just default color
              drawX(
                ctx,
                x + cw / 2,
                y + ch / 2,
                Math.min(cw, ch) * 0.45,
                Math.min(cw, ch) * 0.08,
                COL_X
              );
            }

            if (shake && shake > 0 && state === CellState.Empty) {
              const t = shake / 0.5;
              ctx.fillStyle = p.errorColors?.get(key) ?? '#d88070';
              ctx.globalAlpha = Math.sin(t * Math.PI * 4) * 0.12 * t;
              ctx.fillRect(x + ox, y, cw, ch);
              ctx.globalAlpha = 1;
            }
          }
        }

        // Grid lines
        const gameLeft = p.maxRowClueLen * cw;
        const gameTop = p.maxColClueLen * ch;
        const gameRight = p.totalCols * cw;
        const gameBottom = p.totalRows * ch;
        const lineW = Math.max(1, cw * 0.024);
        const sepW = Math.max(1.5, cw * 0.06);

        ctx.fillStyle = COL_GRID;
        for (let ri = 1; ri < p.gameRows; ri++) {
          if (ri % 5 === 0) continue;
          const y = gameTop + ri * ch;
          ctx.fillRect(0, y - lineW / 2, gameRight, lineW);
        }
        for (let ci = 1; ci < p.gameCols; ci++) {
          if (ci % 5 === 0) continue;
          const x = gameLeft + ci * cw;
          ctx.fillRect(x - lineW / 2, 0, lineW, gameBottom);
        }

        ctx.fillStyle = COL_SEP;
        for (let ri = 5; ri < p.gameRows; ri += 5) {
          const y = gameTop + ri * ch;
          ctx.fillRect(0, y - sepW / 2, gameRight, sepW);
        }
        for (let ci = 5; ci < p.gameCols; ci += 5) {
          const x = gameLeft + ci * cw;
          ctx.fillRect(x - sepW / 2, 0, sepW, gameBottom);
        }

        // Border
        ctx.fillStyle = COL_GRID;
        ctx.fillRect(
          gameLeft + cornerRadius,
          gameTop - lineW / 2,
          gameRight - gameLeft - cornerRadius,
          lineW
        );
        ctx.fillRect(
          gameLeft - lineW / 2,
          gameTop + cornerRadius,
          lineW,
          gameBottom - gameTop - cornerRadius
        );
        ctx.strokeStyle = COL_GRID;
        ctx.lineWidth = lineW;
        ctx.beginPath();
        ctx.arc(
          gameLeft + cornerRadius,
          gameTop + cornerRadius,
          cornerRadius,
          Math.PI,
          Math.PI * 1.5
        );
        ctx.stroke();
        // Right and bottom borders of game area (mirrors left/top borders)
        ctx.fillStyle = COL_GRID;
        ctx.fillRect(gameRight - lineW / 2, gameTop, lineW, gameBottom - gameTop);
        ctx.fillRect(gameLeft, gameBottom - lineW / 2, gameRight - gameLeft, lineW);

        // Particles
        const pts = p.particles;
        if (pts && pts.length > 0) {
          const startT = particleStartRef.current;
          const gravity = 4; // cells/sec²
          const radius = Math.min(cw, ch) * 0.12;
          for (const pt of pts) {
            const elapsed = (time - startT - pt.delay) / 1000;
            if (elapsed < 0) continue;
            if (elapsed > 1) continue;
            const px = (p.maxRowClueLen + pt.x + pt.dx * elapsed) * cw;
            const py =
              (p.maxColClueLen + pt.y + pt.dy * elapsed + 0.5 * gravity * elapsed * elapsed) * ch;
            ctx.globalAlpha = 1 - elapsed;
            ctx.fillStyle = pt.color;
            ctx.beginPath();
            ctx.arc(px, py, radius, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.globalAlpha = 1;
        }

        // Clue numbers
        const fontSize = Math.min(cw, ch) * 0.48;
        ctx.font = `800 ${fontSize}px 'Lilita One', sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        for (let c = 0; c < p.gameCols; c++) {
          const clue = p.colClues[c];
          const offset = p.maxColClueLen - clue.length;
          for (let i = 0; i < clue.length; i++) {
            const isDone =
              p.completedCols[c] || (p.colClueDone?.[c]?.[i] ?? false);
            ctx.fillStyle = isDone ? COL_CLUE_DONE : COL_CLUE;
            ctx.fillText(
              `${clue[i]}`,
              (p.maxRowClueLen + c) * cw + cw / 2,
              (offset + i) * ch + ch / 2
            );
          }
        }
        for (let r = 0; r < p.gameRows; r++) {
          const clue = p.rowClues[r];
          const offset = p.maxRowClueLen - clue.length;
          for (let i = 0; i < clue.length; i++) {
            const isDone =
              p.completedRows[r] || (p.rowClueDone?.[r]?.[i] ?? false);
            ctx.fillStyle = isDone ? COL_CLUE_DONE : COL_CLUE;
            ctx.fillText(
              `${clue[i]}`,
              (offset + i) * cw + cw / 2,
              (p.maxColClueLen + r) * ch + ch / 2
            );
          }
        }

        // Span hints — minimum space each clue needs: sum + gaps
        if (p.showSpanHints) {
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';

          // Separator line between span column and row clues
          ctx.fillStyle = COL_GRID;
          ctx.fillRect(cw - lineW / 2, gameTop, lineW, gameBottom - gameTop);
          // Separator line between span row and column clues
          ctx.fillRect(gameLeft, ch - lineW / 2, gameRight - gameLeft, lineW);

          ctx.font = `800 ${fontSize}px 'Lilita One', sans-serif`;
          ctx.fillStyle = '#b89060';

          // Row span hints (first column slot)
          for (let r = 0; r < p.gameRows; r++) {
            const clue = p.rowClues[r];
            const span = clue.reduce((a, b) => a + b, 0) + Math.max(0, clue.length - 1);
            ctx.fillText(`${span}`, cw / 2, (p.maxColClueLen + r) * ch + ch / 2);
          }

          // Column span hints (first row slot)
          for (let c = 0; c < p.gameCols; c++) {
            const clue = p.colClues[c];
            const span = clue.reduce((a, b) => a + b, 0) + Math.max(0, clue.length - 1);
            ctx.fillText(`${span}`, (p.maxRowClueLen + c) * cw + cw / 2, ch / 2);
          }
        }

        rafRef.current = requestAnimationFrame(draw);
      };

      rafRef.current = requestAnimationFrame(draw);
      return () => cancelAnimationFrame(rafRef.current);
    }, []); // mount only — draw loop reads from propsRef

    return (
      <div
        ref={gridRef}
        tabIndex={tabIndex}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onPointerLeave={handlePointerLeave}
        style={{
          width: boardW,
          maxWidth: '100%',
          aspectRatio: `${totalCols} / ${totalRows}`,
          border: '2px solid var(--board-border)',
          borderRadius: 8,
          overflow: 'hidden',
          touchAction: 'none',
          userSelect: 'none',
          WebkitTapHighlightColor: 'transparent',
          outline: 'none',
          cursor: 'pointer',
          marginTop: 0,
          flexShrink: 0,
        }}
      >
        <canvas
          ref={canvasRef}
          style={{ display: 'block', width: '100%', height: '100%' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onContextMenu={(e) => e.preventDefault()}
        />
      </div>
    );
  }
);

/* ---------- FillIndicator ---------- */

export function FillIndicator() {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        borderRadius: 6,
        background: COL_FILLED,
      }}
    />
  );
}
